import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, ShieldCheck, ShieldOff } from "lucide-react";
import { api } from "@/lib/api";
import { PageHeader } from "@/components/PageHeader";
import { MfaEnroll } from "@/components/auth/MfaEnroll";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { fmtDate } from "@/lib/format";
import { getErrorMessage } from "@/lib/errors";

/** Two-step sign-in for the person who is signed in. */
export default function Security() {
  const [status, setStatus] = useState(null);
  const [enrolling, setEnrolling] = useState(false);
  const [asking, setAsking] = useState(null);                    // null | "disable" | "codes"
  const [newCodes, setNewCodes] = useState(null);
  const load = useCallback(async () => { try { setStatus((await api.get("/auth/mfa/status")).data); } catch (err) { toast.error(getErrorMessage(err)); } }, []);
  useEffect(() => { load(); }, [load]);
  if (!status) return <p className="text-sm text-muted-foreground">Loading…</p>;
  return (
    <div className="space-y-5" data-testid="security-page">
      <PageHeader title="Two-step sign-in" description="Sign in with a password and a second code, so a stolen password alone is not enough." />
      <div className="max-w-xl space-y-4 rounded-xl border border-border bg-card p-5">
        {status.enabled ? (
          <>
            <div className="flex items-center gap-2" data-testid="mfa-status-on"><ShieldCheck className="h-5 w-5 text-emerald-600" /><p className="text-sm font-medium">Two-step sign-in is on</p></div>
            <p className="text-xs text-muted-foreground" data-testid="mfa-method-line">{status.method === "EMAIL" ? `Codes are e-mailed to ${status.sent_to}.` : status.method === "SMS" ? `Codes are texted to ${status.sent_to}.` : "Codes come from your authenticator app."}</p>
            <p className="text-xs text-muted-foreground">Turned on {status.enrolled_at ? fmtDate(status.enrolled_at) : ""}. {status.backup_codes_left} backup code{status.backup_codes_left === 1 ? "" : "s"} left.</p>
            <div className="flex flex-wrap gap-2">
              <Button variant="outline" size="sm" onClick={() => setAsking("codes")} data-testid="mfa-new-codes">Get new backup codes</Button>
              {status.required
                ? <p className="text-xs text-muted-foreground" data-testid="mfa-required-note">Your organisation requires two-step sign-in, so it cannot be turned off.</p>
                : <Button variant="outline" size="sm" onClick={() => setAsking("disable")} data-testid="mfa-disable"><ShieldOff className="h-3.5 w-3.5" /> Turn off</Button>}
            </div>
            {newCodes && (
              <div className="space-y-2" data-testid="mfa-new-codes-list"><p className="text-sm font-medium">Your new backup codes (the old ones no longer work)</p>
                <ul className="grid grid-cols-2 gap-1 rounded-lg border border-border bg-muted/40 p-3 font-mono text-sm">{newCodes.map((c) => <li key={c}>{c}</li>)}</ul></div>)}
          </>
        ) : enrolling ? (
          <MfaEnroll onDone={() => { setEnrolling(false); load(); }} />
        ) : (
          <>
            <div className="flex items-center gap-2" data-testid="mfa-status-off"><ShieldOff className="h-5 w-5 text-amber-600" /><p className="text-sm font-medium">Two-step sign-in is off</p></div>
            {status.required && <p className="text-xs text-amber-700" data-testid="mfa-required-warning">Your organisation requires it. Set it up now.</p>}
            <Button onClick={() => setEnrolling(true)} data-testid="mfa-turn-on">Turn on</Button>
          </>)}
      </div>
      {asking && <ReauthDialog kind={asking} method={status.method} onClose={() => setAsking(null)} onDone={(codes) => { setAsking(null); if (codes) setNewCodes(codes); load(); }} />}
    </div>
  );
}

function ReauthDialog({ kind, method, onClose, onDone }) {
  const sends = method === "EMAIL" || method === "SMS";
  const [sentTo, setSentTo] = useState(null);
  const sendCode = async () => { try { const { data } = await api.post("/auth/mfa/send-code"); setSentTo(data.sent_to); toast.success(`Code sent to ${data.sent_to}`); } catch (err) { toast.error(getErrorMessage(err)); } };
  const [password, setPassword] = useState("");
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const go = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(kind === "disable" ? "/auth/mfa/disable" : "/auth/mfa/backup-codes", { password, code });
      toast.success(kind === "disable" ? "Two-step sign-in is off" : "New backup codes made");
      onDone(data.backup_codes);
    } catch (err) { toast.error(getErrorMessage(err)); } finally { setBusy(false); }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-sm" data-testid="mfa-reauth">
        <DialogHeader><DialogTitle>{kind === "disable" ? "Turn off two-step sign-in" : "Get new backup codes"}</DialogTitle><DialogDescription>{sends ? "Confirm it is you: your password and a code we send you (or a backup code)." : "Confirm it is you: your password and a code from the app (or a backup code)."}</DialogDescription></DialogHeader>
        <div className="space-y-3">
          <div><Label>Password</Label><Input type="password" value={password} onChange={(e) => setPassword(e.target.value)} className="mt-1.5" data-testid="mfa-reauth-password" /></div>
          <div><Label>Code</Label><Input value={code} onChange={(e) => setCode(e.target.value)} className="mt-1.5 font-mono" data-testid="mfa-reauth-code" />
            {sends && <button type="button" className="mt-1 text-xs text-primary underline" onClick={sendCode} data-testid="mfa-reauth-send">{sentTo ? `Sent to ${sentTo}. Send again` : "Send me a code"}</button>}</div>
        </div>
        <DialogFooter><Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button disabled={busy || !password || code.trim().length < 6} onClick={go} data-testid="mfa-reauth-submit">{busy && <Loader2 className="h-4 w-4 animate-spin" />} Confirm</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
